// Read receipts this client put on the wire, by scope, so a `read_broadcast` that
// carries one of them is recognised as this client's own echo (#514). The relay
// never echoes a socket's own read; the daemon broadcasts to every socket of the
// user, the sender included, and the two must be told apart the same way. Every
// receipt carries a client stamp (Date.now() at send), so equality is exact.
const KEEP = 16;

export class SentReadStamps {
  private readonly byScope = new Map<string, number[]>();

  record(scope: string, at: number): void {
    const list = this.byScope.get(scope) ?? [];
    list.push(at);
    if (list.length > KEEP) list.splice(0, list.length - KEEP);
    this.byScope.set(scope, list);
  }

  /** True when `at` is a stamp this client sent for `scope`. */
  isOwn(scope: string, at: number): boolean {
    return this.byScope.get(scope)?.includes(at) ?? false;
  }

  clear(): void {
    this.byScope.clear();
  }
}

// When each scope's read receipts were acked (the relay answers after the write commits).
export class ReadReceiptAcks {
  private readonly inFlight = new Map<string, number>();
  private readonly settledAt = new Map<string, number>();

  sent(scope: string): void {
    this.inFlight.set(scope, (this.inFlight.get(scope) ?? 0) + 1);
  }

  /** Acked, refused, timed out or dropped. */
  settled(scope: string): void {
    const left = (this.inFlight.get(scope) ?? 1) - 1;
    if (left > 0) this.inFlight.set(scope, left);
    else this.inFlight.delete(scope);
    this.settledAt.set(scope, Date.now());
  }

  /** Infinity while unacked, else the last settle time. */
  at(scope: string): number | undefined {
    if (this.inFlight.has(scope)) return Number.POSITIVE_INFINITY;
    return this.settledAt.get(scope);
  }

  clear(): void {
    this.inFlight.clear();
    this.settledAt.clear();
  }
}
